"use client";

import React, {
  useRef,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslate } from "../../lib/translation";
import { cn } from "../../lib/cn";
import Icon from "@dt/Icon";
import {
  FieldShell,
  fieldDescribedBy,
  fieldIds,
  fieldSizeClass,
  fieldStatusClass,
  resolveFieldStatus,
  type FieldAction,
  type FieldSize,
  type FieldStatus,
} from "@dt/FieldShell";
import styles from "./ComboboxField.module.css";
import { useComboboxDropdown } from "./useComboboxDropdown";
import {
  firstEnabledOptionIndex,
  focusLeftField,
  lastEnabledOptionIndex,
  stepEnabledOptionIndex,
} from "./comboboxOptions";

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface ComboboxProps {
  /** Field label; associates the trigger */
  label: string;
  /** Selectable options */
  options: ComboboxOption[];
  /** Selected option value (controlled); "" shows the placeholder */
  value: string;
  /** Called with the chosen value */
  onValueChange: (value: string) => void;
  /** Explicit control id (wires the label); auto-generated when omitted */
  id?: string;
  /** Shown when no value is selected */
  placeholder?: string;
  /** Assistive text below the field */
  helperText?: string;
  /** Error message; renders above the helper line and sets aria-invalid */
  error?: string;
  /** Semantic state: colours the border and styles statusMessage. `error` overrides it. */
  status?: FieldStatus;
  /** Message under the field for `status`, above the helper line */
  statusMessage?: string;
  /** Icon buttons at the end of the label row: each runs an operation or opens a menu */
  actions?: FieldAction[];
  /** Marks the field required. @default false */
  required?: boolean;
  /** Disables the control. @default false */
  disabled?: boolean;
  /** Field size shared by every DS field: 40, 48 or 56px tall. @default "md" */
  size?: FieldSize;
  /** Submits the value with a surrounding form, through a hidden input */
  name?: string;
  /** Classes on the field wrapper */
  className?: string;
  /** Extra ids the trigger is described by, after the status and helper text. */
  "aria-describedby"?: string;
}

/** Single-select combobox with portaled dropdown (matches MultiCombobox UX). */
export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>(
  function Combobox(
    {
      label,
      options,
      value,
      onValueChange,
      id: providedId,
      placeholder,
      helperText,
      error,
      status,
      statusMessage,
      actions,
      required = false,
      disabled = false,
      size = "md",
      name,
      className,
      "aria-describedby": externalDescribedBy,
    },
    ref,
  ) {
    const t = useTranslate();
    const generatedId = useId();
    const fieldId = providedId ?? `combobox-${generatedId}`;
    const listboxId = `${fieldId}-listbox`;
    const field = resolveFieldStatus({ status, statusMessage, error });
    const labelId = fieldIds(fieldId).label;

    const [open, setOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    // Typeahead (WAI-ARIA select-only combobox): typed characters build a
    // search string that clears after a pause; the highlight jumps to the
    // first option whose label, or any word in it, starts with the string.
    const typeahead = useRef<{ text: string; timer: ReturnType<typeof setTimeout> | null }>({ text: "", timer: null });

    const selectedLabel = useMemo(
      () => options.find((option) => option.value === value)?.label,
      [options, value],
    );

    const describedBy = fieldDescribedBy(
      fieldId,
      { statusMessage: field.statusMessage, helperText },
      externalDescribedBy,
    );

    const closeDropdown = useCallback(() => {
      setOpen(false);
      setHighlightedIndex(0);
    }, []);

    // Highlight follows the selected value at the moment the dropdown
    // opens. This must NOT live in an effect keyed on `options`: consumers
    // idiomatically build the options array inline, so its identity changes
    // every render and an options-keyed effect resets the highlight on each
    // arrow-key re-render, breaking keyboard navigation entirely.
    // With no selection it starts on the first enabled option.
    const syncHighlightToValue = useCallback(() => {
      const selectedIndex = options.findIndex(
        (option) => option.value === value,
      );
      setHighlightedIndex(
        selectedIndex >= 0 ? selectedIndex : firstEnabledOptionIndex(options),
      );
    }, [options, value]);

    const openDropdown = useCallback(() => {
      if (disabled) return;
      syncHighlightToValue();
      setOpen(true);
    }, [disabled, syncHighlightToValue]);

    const toggleDropdown = useCallback(() => {
      if (disabled) return;
      if (!open) syncHighlightToValue();
      setOpen((current) => !current);
    }, [disabled, open, syncHighlightToValue]);

    const selectOption = useCallback(
      (optionValue: string) => {
        onValueChange(optionValue);
        closeDropdown();
      },
      [closeDropdown, onValueChange],
    );

    const {
      mounted,
      dropdownStyle,
      controlRef,
      listRef,
      containerRef,
    } = useComboboxDropdown(open, options.length, closeDropdown);

    useEffect(() => {
      if (!open || !listRef.current) return;
      const highlighted = listRef.current.querySelector(
        `[data-option-index="${highlightedIndex}"]`,
      );
      highlighted?.scrollIntoView?.({ block: "nearest" });
    }, [highlightedIndex, listRef, open]);

    const findTypeaheadMatch = (search: string): number => {
      const query = search.toLocaleLowerCase();
      const startsWith = (label: string) => label.toLocaleLowerCase().startsWith(query);
      const wordStartsWith = (label: string) =>
        label
          .toLocaleLowerCase()
          .split(/[\s/()_,.-]+/)
          .some((word) => word.startsWith(query));
      const enabled = (index: number) => !options[index].disabled;
      const byLabel = options.findIndex((option, index) => enabled(index) && startsWith(option.label));
      if (byLabel >= 0) return byLabel;
      return options.findIndex((option, index) => enabled(index) && wordStartsWith(option.label));
    };

    const handleTypeahead = (character: string) => {
      const state = typeahead.current;
      if (state.timer) clearTimeout(state.timer);
      state.text += character;
      state.timer = setTimeout(() => {
        state.text = "";
        state.timer = null;
      }, 700);
      const match = findTypeaheadMatch(state.text);
      if (!open) openDropdown();
      if (match >= 0) setHighlightedIndex(match);
    };

    useEffect(() => () => {
      if (typeahead.current.timer) clearTimeout(typeahead.current.timer);
    }, []);

    const handleTriggerKeyDown = (
      event: React.KeyboardEvent<HTMLButtonElement>,
    ) => {
      if (disabled) return;

      const isPrintable = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
      // A space while a search is being typed is part of it ("new y").
      if (isPrintable && (event.key !== " " || typeahead.current.text)) {
        event.preventDefault();
        handleTypeahead(event.key);
        return;
      }

      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          if (!open) {
            openDropdown();
            return;
          }
          // Disabled options are skipped, as typeahead skips them.
          setHighlightedIndex((current) => {
            const next = stepEnabledOptionIndex(options, current, 1);
            return next < 0 ? current : next;
          });
          break;
        }
        case "ArrowUp": {
          event.preventDefault();
          if (!open) {
            openDropdown();
            return;
          }
          setHighlightedIndex((current) => {
            const next = stepEnabledOptionIndex(options, current, -1);
            return next < 0 ? current : next;
          });
          break;
        }
        case "Home":
        case "End": {
          event.preventDefault();
          if (!open) openDropdown();
          const edge =
            event.key === "Home"
              ? firstEnabledOptionIndex(options)
              : lastEnabledOptionIndex(options);
          if (edge >= 0) setHighlightedIndex(edge);
          break;
        }
        case "Tab": {
          // Focus moves on (no preventDefault); the list must not stay open
          // behind it.
          if (open) closeDropdown();
          break;
        }
        case "Enter":
        case " ": {
          event.preventDefault();
          if (!open) {
            openDropdown();
            return;
          }
          const option = options[highlightedIndex];
          if (option && !option.disabled) {
            selectOption(option.value);
          }
          break;
        }
        case "Escape": {
          event.preventDefault();
          closeDropdown();
          break;
        }
        default:
          break;
      }
    };

    const activeDescendant =
      open && options[highlightedIndex]
        ? `${fieldId}-option-${options[highlightedIndex].value}`
        : undefined;

    const dropdown = open && dropdownStyle && (
      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-label={label}
        className={styles.dropdown}
        style={{
          top: dropdownStyle.top,
          left: dropdownStyle.left,
          width: dropdownStyle.width,
        }}
        data-testid={`${fieldId}-dropdown`}
        data-state="open"
        data-lenis-prevent=""
        data-lenis-prevent-wheel=""
        data-lenis-prevent-touch=""
      >
        {options.length === 0 ? (
          <li className={styles.empty} role="presentation">
            {t("multiComboboxNoResults", "No matching options")}
          </li>
        ) : (
          options.map((option, index) => {
            const selected = value === option.value;
            const optionDisabled = disabled || option.disabled;
            const optionId = `${fieldId}-option-${option.value}`;

            return (
              <li key={option.value} role="presentation">
                <button
                  id={optionId}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  data-option-index={index}
                  aria-selected={selected}
                  disabled={optionDisabled}
                  className={cn(
                    styles.option,
                    selected && styles.optionSelected,
                    index === highlightedIndex && styles.optionHighlighted,
                  )}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectOption(option.value)}
                >
                  <span className={styles.optionLabel}>{option.label}</span>
                  {selected ? (
                    <Icon
                      name="check"
                      className={styles.optionCheck}
                      ariaLabel=""
                    />
                  ) : null}
                </button>
              </li>
            );
          })
        )}
      </ul>
    );

    return (
      <FieldShell
        ref={containerRef}
        className={className}
        controlId={fieldId}
        label={label}
        required={required}
        disabled={disabled}
        status={field.status}
        statusMessage={field.statusMessage}
        helperText={helperText}
        actions={actions}
      >
        <div
          className={styles.wrapper}
          onBlur={(event) => {
            if (
              open &&
              focusLeftField(
                event.relatedTarget,
                event.currentTarget,
                listRef.current,
              )
            ) {
              closeDropdown();
            }
          }}
        >
          <div
            ref={controlRef}
            className={cn(
              styles.control,
              fieldSizeClass(size),
              open && styles.controlOpen,
              fieldStatusClass(field.status),
              disabled && styles.disabled,
            )}
            data-state={open ? "open" : "closed"}
            data-field-status={field.status}
          >
            <button
              ref={ref}
              id={fieldId}
              type="button"
              role="combobox"
              className={styles.trigger}
              disabled={disabled}
              aria-labelledby={labelId}
              aria-describedby={describedBy}
              aria-invalid={field.status === "error" ? true : undefined}
              aria-required={required || undefined}
              aria-expanded={open}
              aria-controls={listboxId}
              aria-activedescendant={activeDescendant}
              data-testid={`${fieldId}-trigger`}
              onClick={() => toggleDropdown()}
              onKeyDown={handleTriggerKeyDown}
            >
              <span
                className={cn(
                  styles.triggerLabel,
                  !selectedLabel && placeholder && styles.triggerPlaceholder,
                )}
              >
                {selectedLabel ?? placeholder ?? ""}
              </span>
            </button>

            <button
              type="button"
              data-chevron-toggle
              className={styles.chevronButton}
              aria-label={t("multiComboboxToggleOptions", "Toggle options")}
              aria-expanded={open}
              disabled={disabled}
              tabIndex={-1}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                toggleDropdown();
              }}
            >
              <Icon
                name="caret-down"
                className={cn(styles.chevron, open && styles.chevronOpen)}
                ariaLabel=""
              />
            </button>
          </div>

          {mounted && dropdown && createPortal(dropdown, document.body)}
        </div>
        {name ? <input type="hidden" name={name} value={value} /> : null}
      </FieldShell>
    );
  },
);

Combobox.displayName = "Combobox";

export default Combobox;
